import React, { useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import StoredFile from './StoredFile';
import {
    proofRounds, proofDocuments, payoutLabel, formatDate, formatInr, toothLabel, PROOF_STATUS_LABEL, PROOF_STATUS_STYLE, TONE,
} from './reimbursementStatus';
import { SECTION_HEADING } from './FormShell';

const DECIDED = { accepted: 'Accepted', rejected: 'Not accepted', reupload_requested: 'Re-upload requested' };

// The procedures one proof covers: as the API lists them, else the Reimbursement's procedures that
// point at this proof.
const coveredProcedures = (round, reimbursement) => {
    if (Array.isArray(round.procedures) && round.procedures.length) return round.procedures;
    const ids = (round.procedure_ids || []).map(String);
    return (reimbursement.procedures || []).filter((p) => (p.claim_id && String(p.claim_id) === String(round._id)) || ids.includes(String(p._id)));
};

const Row = ({ label, children }) => (
    <div className="flex gap-3">
        <dt className="w-20 flex-shrink-0 text-portal-sub">{label}</dt>
        <dd className="min-w-0 text-portal-ink break-words">{children}</dd>
    </div>
);

// One proof: what it covers, what the member reported, where the payout goes, the reviewer's
// decision, and thumbnails of its documents. The thumbnails (signed links) load only once opened.
const ProofRound = ({ round, reimbursement, open, onToggle }) => {
    const status = round.review_status;
    const amount = round.approved_amount;
    const procedures = coveredProcedures(round, reimbursement);
    const documents = proofDocuments(round);
    const payout = payoutLabel(round);

    return (
        <details
            open={open}
            onToggle={(event) => onToggle(event.currentTarget.open)}
            className="group border border-portal-border rounded-xl bg-white"
            data-testid="proof-round"
        >
            <summary className="list-none [&::-webkit-details-marker]:hidden cursor-pointer px-3 py-2.5 flex items-start justify-between gap-3 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-portal-purple">
                <div className="min-w-0">
                    <p className="text-sm font-bold text-portal-ink">Proof #{round.round}</p>
                    <p className="text-xs text-portal-sub">{round.submitted_at ? `Submitted ${formatDate(round.submitted_at)}` : 'Submitted'}</p>
                </div>
                <div className="flex items-start gap-2 flex-shrink-0">
                    <div className="flex flex-col items-end gap-1">
                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${PROOF_STATUS_STYLE[status] || TONE.neutral}`} data-testid="proof-round-outcome">
                            {PROOF_STATUS_LABEL[status] || 'Submitted'}
                        </span>
                        {typeof amount === 'number' && (
                            <span className="text-xs font-semibold text-portal-ink">
                                {status === 'accepted' ? `Accepted ${formatInr(amount)}` : `Covers ${formatInr(amount)}`}
                            </span>
                        )}
                    </div>
                    <ChevronDown className="w-4 h-4 mt-0.5 text-portal-sub transition-transform group-open:rotate-180" aria-hidden="true" />
                </div>
            </summary>

            <div className="px-3 pb-3 pt-1 space-y-3 text-xs border-t border-portal-border">
                {procedures.length > 0 && (
                    <div>
                        <p className="font-semibold text-portal-ink mt-2 mb-1">Covers</p>
                        <ul className="space-y-0.5">
                            {procedures.map((p) => (
                                <li key={p._id} className="flex justify-between gap-3 text-portal-ink">
                                    <span className="min-w-0">{p.treatment} · {toothLabel(p.tooth)}</span>
                                    {typeof p.approved_amount === 'number' && <span className="flex-shrink-0">{formatInr(p.approved_amount)}</span>}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <dl className="space-y-1">
                    {round.procedure_performed && (
                        <Row label="Treatment">
                            {[
                                round.procedure_performed,
                                round.treatment_completion_date && `completed ${formatDate(round.treatment_completion_date, { calendarDay: true })}`,
                                round.total_cost !== undefined && round.total_cost !== null && round.total_cost !== '' && `${formatInr(round.total_cost)} paid`,
                            ].filter(Boolean).join(' · ')}
                        </Row>
                    )}
                    {round.clinic_name && <Row label="Clinic">{round.clinic_name}</Row>}
                    {payout && <Row label="Payout">{payout}</Row>}
                    {DECIDED[status] && round.decided_at && <Row label="Decision">{`${DECIDED[status]} on ${formatDate(round.decided_at)}`}</Row>}
                </dl>

                {round.decision_note && (
                    <p className={`border rounded-lg px-2.5 py-2 ${status === 'rejected' ? TONE.danger : status === 'reupload_requested' ? TONE.action : TONE.neutral}`}>
                        Reviewer note: “{round.decision_note}”
                    </p>
                )}

                {open && documents.length > 0 && (
                    <div className="grid grid-cols-4 gap-2">
                        {documents.map((doc) => (
                            <figure key={`${doc.field}-${doc.index}`} className="min-w-0">
                                <StoredFile
                                    variant="thumb"
                                    reimbursementId={reimbursement._id}
                                    claimId={round._id}
                                    field={doc.field}
                                    index={doc.index}
                                    storedUrl={doc.url}
                                    label={doc.label}
                                />
                                <figcaption className="mt-1 text-[11px] leading-tight">
                                    <span className="block font-semibold text-portal-ink break-words">{doc.label}</span>
                                    {doc.name !== doc.label && (
                                        <span className="block text-portal-sub truncate mt-0.5" data-testid="proof-document-name" title={doc.name}>{doc.name}</span>
                                    )}
                                </figcaption>
                            </figure>
                        ))}
                    </div>
                )}
            </div>
        </details>
    );
};

// "Proofs submitted (N)": every proof of treatment on this Reimbursement, newest first and open.
const ProofRounds = ({ reimbursement }) => {
    const rounds = [...proofRounds(reimbursement)].reverse();
    const newest = rounds[0]?._id || null;
    const [openIds, setOpenIds] = useState(() => new Set(newest ? [newest] : []));

    // A proof added while the card is shown opens too.
    useEffect(() => {
        if (newest) setOpenIds((prev) => (prev.has(newest) ? prev : new Set([...prev, newest])));
    }, [newest]);

    if (!rounds.length) return null;

    const toggle = (id, isOpen) => setOpenIds((prev) => {
        if (prev.has(id) === isOpen) return prev;
        const next = new Set(prev);
        if (isOpen) next.add(id);
        else next.delete(id);
        return next;
    });

    return (
        <section className="mt-4" data-testid="proof-rounds">
            <p className={`${SECTION_HEADING} mb-2`}>Proofs submitted ({rounds.length})</p>
            <div className="space-y-2">
                {rounds.map((round) => (
                    <ProofRound
                        key={round._id || round.round}
                        round={round}
                        reimbursement={reimbursement}
                        open={openIds.has(round._id)}
                        onToggle={(isOpen) => toggle(round._id, isOpen)}
                    />
                ))}
            </div>
        </section>
    );
};

export default ProofRounds;
